import { logSessionInfo } from '../services/logger';
import { getUserData } from './API';

export const NetworkStatus = Object.freeze({
  Normal: 'Normal',
  Bad: 'Bad',
});

const audioCleanUpThresholdMs = 2000; // Threshold for what we consider stale audio object

export default class NetworkMonitoringService {
  status = NetworkStatus.Normal;

  audioQueue = []; // Holds representations of audio segments that have been sent to server, but have not yet been ack'd
  audioQueueLengthCache = []; // Stores audio queue lengths to monitor how it is changing over time
  audioMonitoringActive = false; // If the service should process updates and analysis
  
  badNetworkPending = false; // If we are in process of checking if we should enter bad network state
  badNetworkPendingTracker = []; // Stores audio queue lengths while we check if the network is bad
  normalNetworkPendingTracker = []; // Stores audio queue lengths while we check if the network is back to normal

  audioQueueAnalyticsSnapshots = []; // Stores snapshots of the audio queue length (occasionally, this will be sent in an analytics event and then reset)
  dequeueAnalytics = []; // Stores dequeue IDs for analysis
  onDecodedAnalytics = []; // Stores onDecoded events for analysis

  badNetworkCount = 0;

  analyticsQueue = []; // If analytics failed to send (due to poor network), we store them here to try again when returning to stable network

  networkStatusCallback = () => {}; // Callback to notify listeners of network status change

  constructor(networkStatusCallback) {
    this.logAnalytics('network_monitoring_service_initialized');
    this.setAudioMonitoringActive(false);
    this.networkStatusCallback = networkStatusCallback;
    console.log("[NetworkMonitoringService] Initialized");
  }

  /**
 * Current network status setter
 * @returns {boolean}
 */
  setNetworkStatus = (newStatus) => {
    const oldStatus = this.status;
    this.status = newStatus;
    switch (this.status) {
      case NetworkStatus.Bad:
        this.badNetworkCount++;
        break;
    }

    this.logAnalytics('network_status_changed', {
      event: newStatus,
    })

    console.log(`[NetworkMonitoringService] Status changed: ${newStatus}`);

    if (newStatus === NetworkStatus.Normal) {
      for (const analyticsEvent of this.analyticsQueue) {
        this.logAnalytics(
          analyticsEvent.eventName,
          analyticsEvent.data,
          analyticsEvent.timestamp
        );
      }

      this.analyticsQueue = [];
    }

    if (getUserData().showBadNetworkResponse && this.networkStatusCallback) {
      this.networkStatusCallback(oldStatus, newStatus);
    }
  }

  /**
 * Does any necessary clean up work when this NMS instance is no longer needed.
 * @returns {boolean}
 */
  cleanUp() {
    this.logAnalytics('network_monitoring_service_cleanup');
    console.log("[NetworkMonitoringService] Cleaning up")
  }

  /**
 * Saves analytics using logSessionInfo, retries on fail, and then adds to analyticsQueue for more retries on continued fail
 * @param {string} eventName - Name of analytics event
 * @param {Object} data - Extra data to add to event
 * @param {number} timestampOverride - Unix timestamp for event
 * @param {boolean} isRetry - If this attempt is a retry. If it is not a retry, then on fail we retry immediately. If it is a retry, then on fail we add to analyticsQueue for future attempts on stable network.
 * @returns {null}
 */
  logAnalytics(eventName, data = {}, timestamp = Date.now(), isRetry = false) {
    logSessionInfo(eventName, { ...data, timestamp }).then((success) => {
      if (!success) {
        if (!isRetry) {
          const retrying = true;
          this.logAnalytics(eventName, data, timestamp, retrying);
        } else {
          this.analyticsQueue.push({
            eventName,
            data,
            timestamp
          })
        }
      }
    });
  }

  /**
 * Checks if the parameters NMS is watching currently indicate slow network.
 * @returns {boolean}
 */
  badNetworkCriteriaMet() {
    if (this.audioQueueLengthCache.length < 4) {
      // We should not be checking badNetworkCriteriaMet if audioQueueLengthCache has less than 4 elements
      return false;
    }

    // Get the last four elements in the audioQueueLengthCache
    const lastFourElements = this.audioQueueLengthCache.slice(-4);

    // Check if any element is lower than the previous element, indicating that the queue is decreasing over time (i.e. NOT bad network)
    const anyElementIsDescending = lastFourElements.some((num, index) => {
      return index > 0 && num < lastFourElements[index - 1];
    });

    // Check if the final element is at least 2 greater than the first element, indicating that the audio queue is growing rapidly enough to cause issues (i.e. bad network)
    const elementsIncreasingQuickly = lastFourElements[3] - lastFourElements[0] >= 2;

    return !anyElementIsDescending && elementsIncreasingQuickly;
  }

  // ***********************
  // Audio queue monitoring
  // ***********************

  /**
 * Enqueues a new audio segment representation and stores the time that it was enqueued.
 * @param {number} audioId - Identifier for an audio segment in the audioQueue. This will be the sequenceNumber from SpeechStream.js
 * @returns {undefined}
 */
  enqueueAudio(audioId, analyticsData = {}) {
    if (!this.audioMonitoringActive) {
      return;
    }
    
    const audio = {
        id: audioId,
        timestamp: Date.now()
    };
    this.audioQueue.push(audio);
    this.audioQueueLengthCache.push(this.audioQueue.length);

    // Uncomment if we need to analyze audio queue length

    // this.audioQueueAnalyticsSnapshots.push({
    //   queueLength: this.audioQueue.length,
    //   lastId: audioId,
    //   timestamp: Date.now(),
    //   extraData: {
    //     ...analyticsData
    //   }
    // });

    this.analyzeAudioQueue();
  }

  /**
 * Dequeues an audio segment representation based on the provided id.
 * @param {number} audioId - Identifier for an audio segment in the audioQueue. This will be the sequenceNumber from SpeechStream.js
 * @returns {undefined}
 */
  dequeueAudio(audioId, analyticsData = {}) {
    const index = this.audioQueue.findIndex(audio => audio.id === audioId);

    if (index !== -1) {
      this.audioQueue.splice(index, 1);
    }

    // Uncomment if we need to analyze audio queue length
    // this.dequeueAnalytics.push({
    //   lastId: audioId,
    //   timestamp: Date.now(),
    //   extraData: {
    //     ...analyticsData
    //   }
    // });
  }

  /**
 * Determines if we should enter bad network state due to backed up audio queue or return to normal state after audio queue clears.
 * @returns {undefined}
 */
  analyzeAudioQueue() {
    if (this.audioQueueLengthCache.length < 4) {
      // Don't do any analysis until we have at least 4 data points
      return;
    }

    if (this.status === NetworkStatus.Normal && !this.badNetworkPending && this.badNetworkCriteriaMet()) {
      // We'll give the audio queue a few cycles to go back down. If we get enough bad network checks in a row, enter slow network state
      this.badNetworkPending = true;
      this.badNetworkPendingTracker = [];
    } else if (this.badNetworkPending) {
      if (!this.badNetworkCriteriaMet()) {
        // If we ever don't meet the bad network criteria, reset the bad network pending state
        this.badNetworkPendingTracker = [];
        this.badNetworkPending = false;
        return;
      }

      // We must meet the bad network criteria 14 times (about 3 seconds) before we enter bad network state to avoid false positives
      this.badNetworkPendingTracker.push(true);
      if (this.badNetworkPendingTracker.length >= 14) {
        // We've hit enough bad network checks in a row to enter bad network state
        this.setNetworkStatus(NetworkStatus.Bad);
        this.badNetworkPendingTracker = [];
        this.badNetworkPending = false;
      }
    } else if (this.status === NetworkStatus.Bad && !this.normalNetworkPending && !this.badNetworkCriteriaMet()) {
      // Start process of going back to normal network if we continue to meet the criteria for normal network
      this.normalNetworkPending = true;
      this.normalNetworkPendingTracker = [];
    } else if (this.normalNetworkPending) {
      // Ensure the network does not meet bad network criteria for a 6 cycles before moving back to normal state
      if (this.badNetworkCriteriaMet()) {
        // If we ever meet the bad network criteria, reset the normal network pending state
        this.normalNetworkPendingTracker = [];
        this.normalNetworkPending = false;
        return;
      }
      this.normalNetworkPendingTracker.push(true);

      if (this.normalNetworkPendingTracker.length >= 6) {
        // We've passed enough cycles to move back to normal state
        this.setNetworkStatus(NetworkStatus.Normal);
        this.normalNetworkPendingTracker = [];
        this.normalNetworkPending = false;
      }
    }
  }

  /**
 * Sends an analytics events with the current list of audioQueue snapshots, then flushes the list
 * @returns {undefined}
 */
  saveAudioQueueAnalyticsSnapshots() {
    this.logAnalytics('network_monitoring_service_queue_data_dump', {
      enqueueAnalytics: this.audioQueueAnalyticsSnapshots,
      dequeueAnalytics: this.dequeueAnalytics,
      onDecodedAnalytics: this.onDecodedAnalytics
    });
    this.audioQueueAnalyticsSnapshots = [];
    this.dequeueAnalytics = [];
    this.onDecodedAnalytics = [];
  }

  /**
 * audioMonitoringActive setter
 * @param {boolean} isActive - If the service should process updates and analysis
 * @returns {undefined}
 */
  setAudioMonitoringActive(isActive) {
    this.audioMonitoringActive = isActive;
    if (!isActive) {
      // Reset audio monitoring data
      this.audioQueue = [];
      this.audioQueueLengthCache = [];
      this.badNetworkPending = false;
      this.badNetworkPendingTracker = [];
      this.normalNetworkPending = false;
      this.normalNetworkPendingTracker = [];
      this.logAnalytics('network_monitoring_service_inactive');
    } else {
      this.logAnalytics('network_monitoring_service_active');
    }
  }

  /**
   * This function is called when a phrase ends in ReaderManager for end of phrase clean up
   */
  handlePhraseEnd() {
    this.setAudioMonitoringActive(false);
    
    // If we are in bad network state at end of phrase, reset to normal
    if (this.status === NetworkStatus.Bad) {
      this.setNetworkStatus(NetworkStatus.Normal);
    }

    // Uncomment if we need to analyze audio queue length
    // this.saveAudioQueueAnalyticsSnapshots();
  }

  /**
 * Called by SpeechStream when audio is decoded. Only used for analytics.
 * @param {number} audioId - Identifier for an audio segment in the audioQueue. This will be the sequenceNumber from SpeechStream.js
 * @returns {undefined}
 */
  handleOnDecoded() {
    // Uncomment if we need to analyze audio queue length

    // this.onDecodedAnalytics.push({
    //   timestamp: Date.now(),
    // });
  }
}